import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { fetchComments } from '../services/cmsApi';

export default function CommentsList({ refreshTrigger }) {
  const { t, language } = useLanguage();
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(1);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoScrolling, setIsAutoScrolling] = useState(true);
  const carouselRef = useRef(null);
  const autoScrollRef = useRef(null);
  const observerRef = useRef();
  // Removed infinite scroll observer since we only want 5 comments max


  useEffect(() => {
    loadComments();
  }, [language]);

  // Refresh comments when refreshTrigger changes (new comment added)
  useEffect(() => {
    if (refreshTrigger) {
      loadComments();
    }
  }, [refreshTrigger]);

  // Auto-scroll functionality
  useEffect(() => {
    if (comments.length === 0 || !isAutoScrolling) return;

    const interval = setInterval(() => {
      setCurrentIndex(prevIndex => {
        const nextIndex = (prevIndex + 1) % comments.length;
        if (carouselRef.current) {
          const containerWidth = carouselRef.current.offsetWidth;
          const scrollPosition = nextIndex * containerWidth;
          carouselRef.current.scrollTo({
            left: scrollPosition,
            behavior: 'smooth'
          });
        }
        return nextIndex;
      });
    }, 4000); // Auto-scroll every 4 seconds for better UX

    return () => clearInterval(interval);
  }, [comments.length, isAutoScrolling]);

  // Reset auto-scroll when user manually interacts
  const handleManualScroll = () => {
    setIsAutoScrolling(false);
    // Resume auto-scroll after 10 seconds of inactivity
    setTimeout(() => {
      setIsAutoScrolling(true);
    }, 10000);
  };

  // Update current index based on scroll position
  const handleScroll = () => {
    if (carouselRef.current) {
      const scrollLeft = carouselRef.current.scrollLeft;
      const containerWidth = carouselRef.current.offsetWidth;
      const newIndex = Math.round(scrollLeft / containerWidth);
      setCurrentIndex(Math.min(Math.max(newIndex, 0), comments.length - 1));
    }
    handleManualScroll();
  };

  // Pause auto-scroll on hover
  const handleMouseEnter = () => {
    setIsAutoScrolling(false);
  };

  const handleMouseLeave = () => {
    setIsAutoScrolling(true);
  };

  const loadComments = async () => {
    setLoading(true);
    setError(null);
    setPage(1);
    setHasMore(true);
    
    try {
      const response = await fetchComments(language, 5);
      console.log('Comments API Response:', response);
      
      if (response && response.data && Array.isArray(response.data)) {
        console.log('Setting comments:', response.data);
        setComments(response.data);
        setHasMore(false); // Disable infinite loading - only show 5 comments max
      } else {
        console.log('No comments data found in response');
        setComments([]);
        setHasMore(false);
      }
    } catch (error) {
      console.error('Error fetching comments:', error);
      setError('Failed to load comments. Please try again later.');
      setComments([]);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  };

  const loadMoreComments = async () => {
    if (isLoadingMore || !hasMore) return;
    
    setIsLoadingMore(true);
    setError(null);
    
    try {
      const nextPage = page + 1;
      const response = await fetchComments(language, 5, nextPage);
      if (response && response.data && Array.isArray(response.data)) {
        setComments(prev => [...prev, ...response.data]);
        setHasMore(response.data.length === 5);
        setPage(nextPage);
      } else {
        setHasMore(false);
      }
    } catch (error) {
      console.error('Error loading more comments:', error);
      setError('Failed to load more comments. Please try again later.');
    } finally {
      setIsLoadingMore(false);
    }
  };


  if (loading) {
    return (
      <div className="bg-white rounded-2xl p-6 md:p-8 border border-orange-200/40 shadow-xl">
        <div className="text-center mb-12">
          <div className="h-8 bg-gray-300 rounded w-1/3 mx-auto mb-4 animate-pulse"></div>
          <div className="h-6 bg-gray-300 rounded w-1/2 mx-auto mb-6 animate-pulse"></div>
        </div>
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="h-6 bg-gray-300 rounded w-3/4 mx-auto mb-4 animate-pulse"></div>
            <div className="h-6 bg-gray-300 rounded w-1/2 mx-auto mb-8 animate-pulse"></div>
            <div className="h-4 bg-gray-300 rounded w-1/4 mx-auto animate-pulse"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-2xl p-6 md:p-8 border border-orange-200/40 shadow-xl">
        <div className="text-center">
          <div className="text-red-500 mb-4">
            <svg className="mx-auto h-12 w-12 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={loadComments}
            className="bg-gradient-to-r from-[#FF8500] to-[#FF6B00] hover:from-[#FF6B00] hover:to-[#FF8500] text-white font-semibold py-2 px-4 rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-xl"
          >
            {t('comments.retry') || 'Try Again'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl p-6 md:p-8 border border-orange-200/40 shadow-xl">
      <div className="text-center mb-12">
        <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
          {t('comments.title')}
        </h3>
        <p className="text-gray-600 text-lg">
          {t('comments.subtitle')}
        </p>
      </div>

      <div 
        className="relative overflow-hidden"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {/* Navigation Arrows */}
        <button
          onClick={() => {
            const newIndex = currentIndex > 0 ? currentIndex - 1 : comments.length - 1;
            setCurrentIndex(newIndex);
            if (carouselRef.current) {
              const containerWidth = carouselRef.current.offsetWidth;
              const scrollPosition = newIndex * containerWidth;
              carouselRef.current.scrollTo({
                left: scrollPosition,
                behavior: 'smooth'
              });
            }
            handleManualScroll();
          }}
          className="absolute left-2 md:left-4 top-1/2 transform -translate-y-1/2 z-10 w-10 h-10 md:w-12 md:h-12 bg-white hover:bg-gray-50 rounded-full flex items-center justify-center transition-all duration-200 shadow-lg border border-gray-200"
        >
          <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        <button
          onClick={() => {
            const newIndex = currentIndex < comments.length - 1 ? currentIndex + 1 : 0;
            setCurrentIndex(newIndex);
            if (carouselRef.current) {
              const containerWidth = carouselRef.current.offsetWidth;
              const scrollPosition = newIndex * containerWidth;
              carouselRef.current.scrollTo({
                left: scrollPosition,
                behavior: 'smooth'
              });
            }
            handleManualScroll();
          }}
          className="absolute right-2 md:right-4 top-1/2 transform -translate-y-1/2 z-10 w-10 h-10 md:w-12 md:h-12 bg-white hover:bg-gray-50 rounded-full flex items-center justify-center transition-all duration-200 shadow-lg border border-gray-200"
        >
          <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
        
        <div 
          ref={carouselRef}
          className="carousel-container flex space-x-4 overflow-x-auto scrollbar-hide pb-4"
          style={{ scrollBehavior: 'smooth' }}
          onScroll={handleScroll}
        >
          {comments.map((comment, index) => (
            <div
              key={comment.id}
              className="carousel-item comment-item flex-shrink-0 w-full flex flex-col items-center justify-center text-center"
            >
              {/* Quote Text */}
              <blockquote className="text-lg sm:text-xl md:text-2xl text-gray-700 leading-relaxed mb-6 md:mb-8 max-w-4xl font-light px-4">
                "{comment.comment}"
              </blockquote>
              
              {/* Author Info */}
              <div className="text-[#FF8500] font-semibold text-lg">
                {comment.name}
                {comment.job_title && (
                  <span className="text-gray-500 font-normal ml-2">
                    {comment.job_title}
                  </span>
                )}
              </div>
            </div>
          ))}
          
        </div>
        
        {/* Navigation dots - Show dots equal to number of comments */}
        <div className="flex justify-center mt-8 space-x-3">
          {comments.map((_, index) => (
            <button
              key={index}
              onClick={() => {
                setCurrentIndex(index);
                if (carouselRef.current) {
                  const containerWidth = carouselRef.current.offsetWidth;
                  const scrollPosition = index * containerWidth;
                  carouselRef.current.scrollTo({
                    left: scrollPosition,
                    behavior: 'smooth'
                  });
                }
                handleManualScroll();
              }}
              className={`w-3 h-3 rounded-full transition-all duration-300 ${
                index === currentIndex 
                  ? 'bg-[#FF8500] scale-125' 
                  : 'bg-gray-300 hover:bg-gray-400'
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
